/** Letters, digits, and asterisk (Washington licenses may include *). */
export const DRIVER_LICENSE_CHARSET_REGEX = /^[A-Za-z0-9*]+$/;

export type DriverLicenseRule = {
  minLength: number;
  maxLength: number;
};

/** Loose universal rule — formats vary by state and change over time. */
export const DRIVER_LICENSE_RULE: DriverLicenseRule = {
  minLength: 4,
  maxLength: 25,
};

export function getDriverLicenseRule(): DriverLicenseRule {
  return DRIVER_LICENSE_RULE;
}

/** Strip spaces, dashes, and other separators; keep letters, digits, and *. */
export function normalizeDriverLicenseInput(value: string, maxLength?: number): string {
  const normalized = value.replace(/[^A-Za-z0-9*]/g, '').toUpperCase();
  if (maxLength != null && normalized.length > maxLength) {
    return normalized.slice(0, maxLength);
  }
  return normalized;
}

export function isDriverLicenseNumberValid(value: string): boolean {
  return validateDriverLicenseNumber(value) === '';
}

/**
 * Validates a driver's license number with a loose universal format.
 * Returns an empty string when valid, or a user-facing error message.
 */
export function validateDriverLicenseNumber(value: string): string {
  const { minLength, maxLength } = DRIVER_LICENSE_RULE;
  const normalized = normalizeDriverLicenseInput(value.trim(), maxLength);

  if (!normalized) {
    return 'This field is required';
  }

  if (!DRIVER_LICENSE_CHARSET_REGEX.test(normalized)) {
    return "Driver's license number can only contain letters, numbers, and *";
  }

  if (normalized.length < minLength) {
    return `Driver's license number must be at least ${minLength} characters`;
  }

  if (normalized.length > maxLength) {
    return `Driver's license number must be at most ${maxLength} characters`;
  }

  return '';
}

/** Normalized value suitable for API submission. */
export function formatDriverLicenseNumberForSubmit(value: string): string {
  return normalizeDriverLicenseInput(value.trim(), DRIVER_LICENSE_RULE.maxLength);
}
